<script setup lang="ts">
import type { FrontendUserDetailFormState } from '../data/getFormItems'
import type { FrontendUserFormPayload, FrontendUserItem } from '@/api/modules/frontendUser'
import type { ApiRequestError } from '@/api/requestError'
import type { SchemaFormExpose } from '@/components/FaSchemaForm/types'
import apiFrontendUser from '@/api/modules/frontendUser'
import { normalizeApiRequestError } from '@/api/requestError'
import AppAttachmentImageUpload from '@/components/AppAttachmentImageUpload.vue'
import AppRequestState from '@/components/AppDisplay/RequestState.vue'
import FaSchemaForm from '@/components/FaSchemaForm/index.vue'
import { useSchemaFormState } from '@/composables/business/useSchemaFormState'
import getFormItems from '../data/getFormItems'

defineOptions({
  name: 'UserUserDetailForm',
})

const props = defineProps<{
  mode: 'create' | 'edit'
  data?: FrontendUserItem | null
}>()

const formRef = ref<SchemaFormExpose<FrontendUserDetailFormState>>()
const submitting = ref(false)
const submitError = shallowRef<ApiRequestError | null>(null)
const form = reactive<FrontendUserDetailFormState>({} as FrontendUserDetailFormState)

const formOptions = computed(() => ({
  labelWidth: 96,
  cols: 2,
}))

const { items: formItems } = useSchemaFormState<FrontendUserDetailFormState, FrontendUserItem>({
  form,
  formRef,
  getItems: () => getFormItems({
    mode: props.mode,
    isEdit: props.mode === 'edit',
    form,
    detail: props.mode === 'edit' ? props.data ?? undefined : undefined,
    components: {
      AppAttachmentImageUpload,
    },
  }),
  watchSource: () => [props.mode, props.data] as const,
  getDetail: () => (props.mode === 'edit' ? props.data ?? undefined : undefined),
})

async function submit() {
  const valid = await formRef.value?.validate()
  if (!valid) {
    return false
  }

  submitting.value = true
  submitError.value = null
  try {
    const payload: FrontendUserFormPayload = {
      username: form.username.trim(),
      password: form.password?.trim() || undefined,
      nickname: form.nickname.trim(),
      display_name: form.display_name.trim() || null,
      realname: form.realname.trim() || null,
      phone: form.phone.trim(),
      email: form.email.trim() || null,
      avatar: form.avatar.trim() || null,
      gender: Number(form.gender || 0),
      birthday: form.birthday || null,
      signature: form.signature.trim() || null,
      level: Number(form.level || 1),
      points: Number(form.points || 0),
      status: Number(form.status || 1),
      is_logoff: Number(form.is_logoff || 0),
      is_recommend: Number(form.is_recommend || 0),
      province: form.province.trim() || null,
      city: form.city.trim() || null,
      district: form.district.trim() || null,
      address: form.address.trim() || null,
      intro: form.intro.trim() || null,
      introduce: form.introduce.trim() || null,
      constellation: form.constellation.trim() || null,
      occupation: form.occupation.trim() || null,
      height: Number(form.height || 0),
      show_cover: form.show_cover.trim() || null,
      photo_album: form.photo_album,
      interest_tags: form.interest_tags_text
        .split(/[、,，\n]/)
        .map(item => item.trim())
        .filter(Boolean),
      is_high: Number(form.is_high || 0),
      is_new: Number(form.is_new || 0),
    }

    if (props.mode === 'edit' && props.data?.id) {
      await apiFrontendUser.update(props.data.id, payload)
    }
    else {
      await apiFrontendUser.create(payload)
    }
  }
  catch (reason) {
    submitError.value = normalizeApiRequestError(reason)
    return false
  }
  finally {
    submitting.value = false
  }
  return true
}

defineExpose({
  submit,
  submitting,
})
</script>

<template>
  <div class="space-y-4">
    <AppRequestState v-if="submitError" compact kind="error" title="用户资料保存失败" :description="submitError.message" :request-id="submitError.requestId" />
    <FaSchemaForm ref="formRef" v-model="form" :items="formItems" :options="formOptions" />
  </div>
</template>
